import React, { useState, useEffect } from 'react';
import config from '../../config';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Button,
  Chip,
  Divider,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { useLanguage } from '../../contexts/LanguageContext';
import { CustodySettlement } from '../../types/custodySettlements.types';
import { companyInfoService, CompanyInfo } from '../../services/companyInfo.service';
import { custodiesService } from '../../services/custodies.service';

interface Props {
  open: boolean;
  onClose: () => void;
  settlement: CustodySettlement | null;
}

const CustodySettlementPreviewDialog: React.FC<Props> = ({ open, onClose, settlement }) => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);
  const [employeeName, setEmployeeName] = useState('');
  const [employeeNameAr, setEmployeeNameAr] = useState('');

  useEffect(() => {
    if (open && settlement) {
      companyInfoService.getCompanyInfo().then(setCompanyInfo).catch(console.error);
      // جلب اسم الموظف من بيانات العُهدة
      custodiesService.getById(settlement.custodyId).then(custody => {
        setEmployeeName(custody.employeeName || '');
        setEmployeeNameAr(custody.employeeNameAr || '');
      }).catch(console.error);
    }
  }, [open, settlement]);

  if (!settlement) return null;

  const currency = isRTL ? 'د.ك' : 'KWD';
  const operationalDetails = settlement.details?.filter(d => d.expenseType === 'Item') || [];
  const administrativeDetails = settlement.details?.filter(d => d.expenseType === 'Category') || [];
  const fixedAssetDetails = settlement.details?.filter(d => d.expenseType === 'Asset') || [];
  const opTotal = operationalDetails.reduce((s, d) => s + d.amount, 0);
  const adTotal = administrativeDetails.reduce((s, d) => s + d.amount, 0);
  const assetsTotal = fixedAssetDetails.reduce((s, d) => s + d.amount, 0);

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'Draft': return isRTL ? 'مسودة' : 'Draft';
      case 'Approved': return isRTL ? 'معتمد' : 'Approved';
      case 'Posted': return isRTL ? 'مرحّل' : 'Posted';
      default: return status;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Draft': return '#9e9e9e';
      case 'Approved': return '#4caf50';
      case 'Posted': return '#2196f3';
      default: return '#9e9e9e';
    }
  };

  const getStatusChipColor = (status: string): 'default' | 'success' | 'info' | 'warning' => {
    switch (status) {
      case 'Draft': return 'warning';
      case 'Approved': return 'success';
      case 'Posted': return 'info';
      default: return 'default';
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString(isRTL ? 'ar-KW' : 'en-GB');
  };

  const fmt = (n?: number) => (n ?? 0).toFixed(3);

  // ===== طباعة بنافذة جديدة — نفس طريقة مرتجع المشتريات =====
  const handlePrint = () => {
    const printWindow = window.open('', '_blank', 'width=900,height=700');
    if (!printWindow) return;

    const dir = isRTL ? 'rtl' : 'ltr';
    const align = isRTL ? 'right' : 'left';
    const alignOpp = isRTL ? 'left' : 'right';
    const themeColor = '#5c6bc0';

    // بناء صفوف التشغيلي
    let opRows = '';
    operationalDetails.forEach((d, idx) => {
      opRows += `
        <tr>
          <td style="text-align:center;">${idx + 1}</td>
          <td>${isRTL ? (d.itemNameAr || d.itemName) : d.itemName}</td>
          <td style="text-align:center;">${isRTL ? (d.unitNameAr || d.unitName) : d.unitName}</td>
          <td style="text-align:center;font-weight:bold;">${fmt(d.quantity)}</td>
          <td style="text-align:center;font-weight:bold;">${fmt(d.unitPrice)}</td>
          <td style="text-align:center;font-weight:bold;color:${themeColor};">${fmt(d.amount)}</td>
          <td>${isRTL ? (d.branchNameAr || d.branchName || '-') : (d.branchName || '-')}</td>
          <td>${d.receiptNumber || '-'}</td>
          <td style="font-size:11px;">${d.description || '-'}</td>
        </tr>
      `;
    });

    // بناء صفوف الإداري
    let adRows = '';
    administrativeDetails.forEach((d, idx) => {
      adRows += `
        <tr>
          <td style="text-align:center;">${idx + 1}</td>
          <td>${isRTL ? (d.categoryNameAr || d.categoryName) : d.categoryName}</td>
          <td>${d.accountCode} - ${isRTL ? (d.accountNameAr || d.accountName) : d.accountName}</td>
          <td style="text-align:center;font-weight:bold;color:${themeColor};">${fmt(d.amount)}</td>
          <td>${isRTL ? (d.branchNameAr || d.branchName || '-') : (d.branchName || '-')}</td>
          <td>${d.receiptNumber || '-'}</td>
          <td style="font-size:11px;">${d.description || '-'}</td>
        </tr>
      `;
    });

    // بناء صفوف الأصول الثابتة
    let asRows = '';
    fixedAssetDetails.forEach((d, idx) => {
      asRows += `
        <tr>
          <td style="text-align:center;">${idx + 1}</td>
          <td>${isRTL ? (d.fixedAssetCategoryNameAr || d.fixedAssetCategoryName || '-') : (d.fixedAssetCategoryName || '-')}</td>
          <td>${isRTL ? (d.assetNameAr || d.assetName || '-') : (d.assetName || '-')}</td>
          <td style="text-align:center;font-weight:bold;color:#7b1fa2;">${fmt(d.amount)}</td>
          <td>${isRTL ? (d.branchNameAr || d.branchName || '-') : (d.branchName || '-')}</td>
          <td>${d.receiptNumber || '-'}</td>
          <td style="font-size:11px;">${d.description || '-'}</td>
        </tr>
      `;
    });

    printWindow.document.write(`
      <html dir="${dir}">
      <head>
        <title>${isRTL ? 'كشف تسوية عُهدة' : 'Custody Settlement'} - ${settlement.settlementNumber}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Segoe UI', Tahoma, Arial, sans-serif; padding: 20px; direction: ${dir}; color: #333; font-size: 13px; }
          .header { text-align: center; border-bottom: 3px solid ${themeColor}; padding-bottom: 15px; margin-bottom: 15px; }
          .header img { max-height: 60px; margin-bottom: 8px; }
          .header h2 { color: ${themeColor}; margin: 5px 0; font-size: 20px; }
          .header .subtitle { font-size: 11px; color: #666; }
          .doc-title { text-align: center; margin-bottom: 20px; }
          .doc-title h3 { color: ${themeColor}; font-size: 24px; border: 2px solid ${themeColor}; display: inline-block; padding: 8px 30px; border-radius: 8px; }
          .info-grid { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 10px; margin-bottom: 15px; border: 1px solid #ddd; border-radius: 8px; padding: 15px; }
          .info-item label { font-size: 11px; color: #888; display: block; margin-bottom: 2px; }
          .info-item .value { font-size: 14px; font-weight: bold; }
          .info-item .value.primary { color: ${themeColor}; font-size: 16px; }
          .info-item .value.custody { color: #e91e63; font-size: 18px; font-weight: 900; }
          .status-badge { display: inline-block; padding: 3px 12px; border-radius: 12px; font-size: 12px; font-weight: bold; color: white; }
          .section-title { background: ${themeColor}; color: white; padding: 10px 15px; font-weight: bold; font-size: 14px; border-radius: 8px 8px 0 0; }
          .section-title.admin { background: #e91e63; }
          .section-title.asset { background: #7b1fa2; }
          table { width: 100%; border-collapse: collapse; }
          thead th { background: #e8eaf6; color: #283593; padding: 10px 6px; font-size: 11px; text-align: ${align}; border-bottom: 2px solid #9fa8da; }
          thead th.center { text-align: center; }
          thead th.admin { background: #fce4ec; color: #880e4f; border-bottom-color: #f48fb1; }
          thead th.asset { background: #f3e5f5; color: #4a148c; border-bottom-color: #ce93d8; }
          tbody td { padding: 7px 6px; border-bottom: 1px solid #eee; font-size: 12px; }
          tbody tr:nth-child(even) { background: #fafafa; }
          .table-wrapper { border: 1px solid #ddd; border-radius: 0 0 8px 8px; overflow: hidden; margin-bottom: 20px; }
          .total-row { background: ${themeColor}; color: white; }
          .total-row.admin { background: #e91e63; }
          .total-row.asset { background: #7b1fa2; }
          .total-row td { font-weight: bold; padding: 10px 6px; font-size: 14px; }
          .summary-box { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 15px; margin-bottom: 20px; border: 2px solid ${themeColor}; border-radius: 8px; padding: 15px; text-align: center; }
          .summary-box .item label { font-size: 11px; color: #888; display: block; margin-bottom: 4px; }
          .summary-box .item .val { font-size: 22px; font-weight: 900; }
          .summary-box .item .val.total { color: ${themeColor}; }
          .summary-box .item .val.returned { color: #e91e63; }
          .summary-box .item .val.net { color: #4caf50; }
          .summary-box .item .cur { font-size: 12px; color: #666; }
          .notes-box { background: #fff8e1; border: 1px solid #ffe082; border-radius: 8px; padding: 10px; margin-bottom: 15px; }
          .notes-box label { font-size: 11px; color: #888; font-weight: bold; }
          .signatures { display: flex; justify-content: space-between; margin-top: 60px; }
          .sig-block { text-align: center; width: 30%; }
          .sig-line { border-top: 1px solid #333; padding-top: 8px; margin-top: 60px; font-weight: bold; font-size: 12px; }
          .print-date { text-align: ${alignOpp}; font-size: 10px; color: #999; margin-bottom: 10px; }
          @media print { @page { size: A4; margin: 10mm; } }
        </style>
      </head>
      <body>
        <div class="print-date">${isRTL ? 'تاريخ الطباعة:' : 'Print Date:'} ${new Date().toLocaleString(isRTL ? 'ar-KW' : 'en-GB')}</div>

        ${companyInfo ? `
        <div class="header">
          ${companyInfo.logoPath ? `<img src="${config.API_ORIGIN}${companyInfo.logoPath}" onerror="this.style.display='none'" />` : ''}
          <h2>${isRTL ? companyInfo.companyNameAr : companyInfo.companyName}</h2>
          <div class="subtitle">
            ${companyInfo.address ? companyInfo.address + ' | ' : ''}${companyInfo.phone ? companyInfo.phone + ' | ' : ''}${companyInfo.email || ''}${companyInfo.website ? ' | ' + companyInfo.website : ''}
          </div>
        </div>
        ` : ''}

        <div class="doc-title">
          <h3>${isRTL ? 'كشف تسوية عُهدة' : 'Custody Settlement'}</h3>
          <div style="margin-top:10px;font-size:20px;font-weight:900;color:#e91e63;">
            ${isRTL ? (settlement.custodyNameAr || settlement.custodyName) : settlement.custodyName}
          </div>
          ${employeeName ? `
          <div style="margin-top:4px;font-size:16px;font-weight:700;color:#333;">
            👤 ${isRTL ? (employeeNameAr || employeeName) : employeeName}
          </div>
          ` : ''}
        </div>

        <div class="info-grid">
          <div class="info-item">
            <label>${isRTL ? 'رقم الكشف' : 'Settlement #'}</label>
            <div class="value primary">${settlement.settlementNumber}</div>
          </div>
          <div class="info-item">
            <label>${isRTL ? 'التاريخ' : 'Date'}</label>
            <div class="value">${formatDate(settlement.settlementDate)}</div>
          </div>
          <div class="info-item">
            <label>${isRTL ? 'الحالة' : 'Status'}</label>
            <div><span class="status-badge" style="background:${getStatusColor(settlement.status)}">${getStatusLabel(settlement.status)}</span></div>
          </div>
          <div class="info-item">
            <label>${isRTL ? 'العُهدة' : 'Custody'}</label>
            <div class="value custody">${isRTL ? (settlement.custodyNameAr || settlement.custodyName) : settlement.custodyName}</div>
            ${employeeName ? `<div style="font-size:13px;font-weight:700;color:#333;margin-top:2px;">👤 ${isRTL ? (employeeNameAr || employeeName) : employeeName}</div>` : ''}
          </div>
          <div class="info-item">
            <label>${isRTL ? 'الفرع' : 'Branch'}</label>
            <div class="value">${isRTL ? (settlement.branchNameAr || settlement.branchName || '-') : (settlement.branchName || '-')}</div>
          </div>
        </div>

        ${operationalDetails.length > 0 ? `
        <div class="section-title">📦 ${isRTL ? 'المصروفات التشغيلية (أصناف)' : 'Operational Expenses (Items)'}</div>
        <div class="table-wrapper">
          <table>
            <thead>
              <tr>
                <th class="center">#</th>
                <th>${isRTL ? 'الصنف' : 'Item'}</th>
                <th class="center">${isRTL ? 'الوحدة' : 'Unit'}</th>
                <th class="center">${isRTL ? 'الكمية' : 'Qty'}</th>
                <th class="center">${isRTL ? 'السعر' : 'Price'}</th>
                <th class="center">${isRTL ? 'المبلغ' : 'Amount'}</th>
                <th>${isRTL ? 'الفرع' : 'Branch'}</th>
                <th>${isRTL ? 'الإيصال' : 'Receipt'}</th>
                <th>${isRTL ? 'الوصف' : 'Description'}</th>
              </tr>
            </thead>
            <tbody>
              ${opRows}
              <tr class="total-row">
                <td colspan="5" style="text-align:${alignOpp};">${isRTL ? 'إجمالي التشغيلي:' : 'Operational Total:'}</td>
                <td style="text-align:center;font-size:16px;">${fmt(opTotal)} ${currency}</td>
                <td colspan="3"></td>
              </tr>
            </tbody>
          </table>
        </div>
        ` : ''}

        ${administrativeDetails.length > 0 ? `
        <div class="section-title admin">🏢 ${isRTL ? 'المصروفات الإدارية' : 'Administrative Expenses'}</div>
        <div class="table-wrapper">
          <table>
            <thead>
              <tr>
                <th class="center admin">#</th>
                <th class="admin">${isRTL ? 'الفئة' : 'Category'}</th>
                <th class="admin">${isRTL ? 'الحساب' : 'Account'}</th>
                <th class="center admin">${isRTL ? 'المبلغ' : 'Amount'}</th>
                <th class="admin">${isRTL ? 'الفرع' : 'Branch'}</th>
                <th class="admin">${isRTL ? 'الإيصال' : 'Receipt'}</th>
                <th class="admin">${isRTL ? 'الوصف' : 'Description'}</th>
              </tr>
            </thead>
            <tbody>
              ${adRows}
              <tr class="total-row admin">
                <td colspan="3" style="text-align:${alignOpp};">${isRTL ? 'إجمالي الإداري:' : 'Administrative Total:'}</td>
                <td style="text-align:center;font-size:16px;">${fmt(adTotal)} ${currency}</td>
                <td colspan="3"></td>
              </tr>
            </tbody>
          </table>
        </div>
        ` : ''}

        ${fixedAssetDetails.length > 0 ? `
        <div class="section-title asset">🏛️ ${isRTL ? 'الأصول الثابتة' : 'Fixed Assets'}</div>
        <div class="table-wrapper">
          <table>
            <thead>
              <tr>
                <th class="center asset">#</th>
                <th class="asset">${isRTL ? 'الفئة' : 'Category'}</th>
                <th class="asset">${isRTL ? 'اسم الأصل' : 'Asset Name'}</th>
                <th class="center asset">${isRTL ? 'المبلغ' : 'Amount'}</th>
                <th class="asset">${isRTL ? 'الموقع' : 'Location'}</th>
                <th class="asset">${isRTL ? 'الإيصال' : 'Receipt'}</th>
                <th class="asset">${isRTL ? 'الوصف' : 'Description'}</th>
              </tr>
            </thead>
            <tbody>
              ${asRows}
              <tr class="total-row asset">
                <td colspan="3" style="text-align:${alignOpp};">${isRTL ? 'إجمالي الأصول:' : 'Assets Total:'}</td>
                <td style="text-align:center;font-size:16px;">${fmt(assetsTotal)} ${currency}</td>
                <td colspan="3"></td>
              </tr>
            </tbody>
          </table>
        </div>
        ` : ''}

        <div class="summary-box">
          <div class="item">
            <label>${isRTL ? 'إجمالي المصروفات' : 'Total Expenses'}</label>
            <div class="val total">${fmt(settlement.totalExpenses)}</div>
            <div class="cur">${currency}</div>
          </div>
          <div class="item">
            <label>${isRTL ? 'المبلغ المسترد' : 'Returned Amount'}</label>
            <div class="val returned">${fmt(settlement.returnedAmount)}</div>
            <div class="cur">${currency}</div>
          </div>
          <div class="item">
            <label>${isRTL ? 'صافي التسوية' : 'Net Settlement'}</label>
            <div class="val net">${fmt(settlement.totalExpenses + settlement.returnedAmount)}</div>
            <div class="cur">${currency}</div>
          </div>
        </div>

        ${settlement.notes ? `
        <div class="notes-box">
          <label>${isRTL ? 'ملاحظات:' : 'Notes:'}</label>
          <div>${settlement.notes}</div>
        </div>
        ` : ''}

        <div class="signatures">
          <div class="sig-block"><div class="sig-line">${isRTL ? 'المُعدّ' : 'Prepared By'}</div></div>
          <div class="sig-block"><div class="sig-line">${isRTL ? 'المعتمد' : 'Approved By'}</div></div>
          <div class="sig-block"><div class="sig-line">${isRTL ? 'المحاسب' : 'Accountant'}</div></div>
        </div>
      </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      // Don't auto-close — let user review the preview and close manually
    }, 500);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth dir={isRTL ? 'rtl' : 'ltr'}
      PaperProps={{ sx: { borderRadius: 3, bgcolor: isDarkMode ? '#1a1a2e' : 'white' } }}>

      <DialogTitle sx={{
        background: isDarkMode
          ? 'linear-gradient(135deg, #283593 0%, #5c6bc0 100%)'
          : 'linear-gradient(135deg, #5c6bc0 0%, #7986cb 100%)',
        color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ReceiptLongIcon />
          <Box component="span" sx={{ fontWeight: 700 }}>
            {isRTL ? 'معاينة كشف التسوية' : 'Settlement Preview'}
          </Box>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white' }}><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ mt: 3 }}>
        {/* Header Info */}
        <Paper elevation={2} sx={{ p: 3, mb: 3, bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f5f5f5', borderRadius: 2 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>{isRTL ? 'رقم الكشف' : 'Settlement #'}</Typography>
              <Typography variant="h6" fontWeight="bold" color="primary">{settlement.settlementNumber}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>{isRTL ? 'التاريخ' : 'Date'}</Typography>
              <Typography variant="body1" component="div" fontWeight="bold">{formatDate(settlement.settlementDate)}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>{isRTL ? 'الحالة' : 'Status'}</Typography>
              <Box><Chip label={getStatusLabel(settlement.status)} color={getStatusChipColor(settlement.status)} size="small" sx={{ fontWeight: 'bold' }} /></Box>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Custody Name — بارز */}
          <Box sx={{ p: 2, bgcolor: isDarkMode ? 'rgba(233,30,99,0.1)' : '#fce4ec', borderRadius: 1, mb: 2 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>{isRTL ? 'العُهدة' : 'Custody'}</Typography>
            <Typography variant="h5" fontWeight={900} sx={{ color: '#e91e63' }}>
              {isRTL ? (settlement.custodyNameAr || settlement.custodyName) : settlement.custodyName}
            </Typography>
            {employeeName && (
              <Typography variant="body1" fontWeight={700} sx={{ mt: 0.5, color: isDarkMode ? '#ccc' : '#333' }}>
                👤 {isRTL ? (employeeNameAr || employeeName) : employeeName}
              </Typography>
            )}
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>{isRTL ? 'الفرع' : 'Branch'}</Typography>
            <Typography variant="body1" fontWeight="bold">
              {isRTL ? (settlement.branchNameAr || settlement.branchName) : settlement.branchName || '-'}
            </Typography>
          </Box>
        </Paper>

        {/* Operational Details */}
        {operationalDetails.length > 0 && (
          <Paper elevation={2} sx={{ mb: 3, borderRadius: 2, overflow: 'hidden' }}>
            <Box sx={{ bgcolor: isDarkMode ? 'rgba(92,107,192,0.3)' : '#5c6bc0', color: 'white', p: 1.5, fontWeight: 'bold' }}>
              📦 {isRTL ? 'المصروفات التشغيلية (أصناف)' : 'Operational Expenses (Items)'}
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>{isRTL ? 'الصنف' : 'Item'}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>{isRTL ? 'الوحدة' : 'Unit'}</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>{isRTL ? 'الكمية' : 'Qty'}</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>{isRTL ? 'السعر' : 'Price'}</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>{isRTL ? 'المبلغ' : 'Amount'}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>{isRTL ? 'الفرع' : 'Branch'}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>{isRTL ? 'الإيصال' : 'Receipt'}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>{isRTL ? 'الوصف' : 'Desc'}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {operationalDetails.map((d, idx) => (
                    <TableRow key={d.detailId || idx} hover>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell>{isRTL ? (d.itemNameAr || d.itemName) : d.itemName}</TableCell>
                      <TableCell>{isRTL ? (d.unitNameAr || d.unitName) : d.unitName}</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>{fmt(d.quantity)}</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>{fmt(d.unitPrice)}</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold', color: '#5c6bc0' }}>{fmt(d.amount)}</TableCell>
                      <TableCell>{isRTL ? (d.branchNameAr || d.branchName) : d.branchName || '-'}</TableCell>
                      <TableCell>{d.receiptNumber || '-'}</TableCell>
                      <TableCell sx={{ fontSize: '0.75rem' }}>{d.description || '-'}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell colSpan={5} align="right" sx={{ fontWeight: 'bold', bgcolor: isDarkMode ? 'rgba(92,107,192,0.2)' : 'rgba(92,107,192,0.1)' }}>
                      {isRTL ? 'إجمالي التشغيلي:' : 'Operational Total:'}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '1.1rem', color: '#5c6bc0', bgcolor: isDarkMode ? 'rgba(92,107,192,0.2)' : 'rgba(92,107,192,0.1)' }}>
                      {fmt(opTotal)} {currency}
                    </TableCell>
                    <TableCell colSpan={3} sx={{ bgcolor: isDarkMode ? 'rgba(92,107,192,0.2)' : 'rgba(92,107,192,0.1)' }} />
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        {/* Administrative Details */}
        {administrativeDetails.length > 0 && (
          <Paper elevation={2} sx={{ mb: 3, borderRadius: 2, overflow: 'hidden' }}>
            <Box sx={{ bgcolor: isDarkMode ? 'rgba(233,30,99,0.3)' : '#e91e63', color: 'white', p: 1.5, fontWeight: 'bold' }}>
              🏢 {isRTL ? 'المصروفات الإدارية' : 'Administrative Expenses'}
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>{isRTL ? 'الفئة' : 'Category'}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>{isRTL ? 'الحساب' : 'Account'}</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>{isRTL ? 'المبلغ' : 'Amount'}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>{isRTL ? 'الفرع' : 'Branch'}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>{isRTL ? 'الإيصال' : 'Receipt'}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>{isRTL ? 'الوصف' : 'Desc'}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {administrativeDetails.map((d, idx) => (
                    <TableRow key={d.detailId || idx} hover>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell>{isRTL ? (d.categoryNameAr || d.categoryName) : d.categoryName}</TableCell>
                      <TableCell>{d.accountCode} - {isRTL ? (d.accountNameAr || d.accountName) : d.accountName}</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold', color: '#e91e63' }}>{fmt(d.amount)}</TableCell>
                      <TableCell>{isRTL ? (d.branchNameAr || d.branchName) : d.branchName || '-'}</TableCell>
                      <TableCell>{d.receiptNumber || '-'}</TableCell>
                      <TableCell sx={{ fontSize: '0.75rem' }}>{d.description || '-'}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell colSpan={3} align="right" sx={{ fontWeight: 'bold', bgcolor: isDarkMode ? 'rgba(233,30,99,0.15)' : 'rgba(233,30,99,0.08)' }}>
                      {isRTL ? 'إجمالي الإداري:' : 'Administrative Total:'}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '1.1rem', color: '#e91e63', bgcolor: isDarkMode ? 'rgba(233,30,99,0.15)' : 'rgba(233,30,99,0.08)' }}>
                      {fmt(adTotal)} {currency}
                    </TableCell>
                    <TableCell colSpan={3} sx={{ bgcolor: isDarkMode ? 'rgba(233,30,99,0.15)' : 'rgba(233,30,99,0.08)' }} />
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        {/* Fixed Assets Details */}
        {fixedAssetDetails.length > 0 && (
          <Paper elevation={2} sx={{ mb: 3, borderRadius: 2, overflow: 'hidden' }}>
            <Box sx={{ bgcolor: isDarkMode ? 'rgba(123,31,162,0.3)' : '#7b1fa2', color: 'white', p: 1.5, fontWeight: 'bold' }}>
              🏛️ {isRTL ? 'الأصول الثابتة' : 'Fixed Assets'}
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>{isRTL ? 'الفئة' : 'Category'}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>{isRTL ? 'اسم الأصل' : 'Asset Name'}</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>{isRTL ? 'المبلغ' : 'Amount'}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>{isRTL ? 'الموقع' : 'Location'}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>{isRTL ? 'الإيصال' : 'Receipt'}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>{isRTL ? 'الوصف' : 'Desc'}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {fixedAssetDetails.map((d, idx) => (
                    <TableRow key={d.detailId || idx} hover>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell>{isRTL ? (d.fixedAssetCategoryNameAr || d.fixedAssetCategoryName) : d.fixedAssetCategoryName || '-'}</TableCell>
                      <TableCell>{isRTL ? (d.assetNameAr || d.assetName) : d.assetName || '-'}</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold', color: '#7b1fa2' }}>{fmt(d.amount)}</TableCell>
                      <TableCell>{isRTL ? (d.branchNameAr || d.branchName) : d.branchName || '-'}</TableCell>
                      <TableCell>{d.receiptNumber || '-'}</TableCell>
                      <TableCell sx={{ fontSize: '0.75rem' }}>{d.description || '-'}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell colSpan={3} align="right" sx={{ fontWeight: 'bold', bgcolor: isDarkMode ? 'rgba(123,31,162,0.15)' : 'rgba(123,31,162,0.08)' }}>
                      {isRTL ? 'إجمالي الأصول:' : 'Assets Total:'}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '1.1rem', color: '#7b1fa2', bgcolor: isDarkMode ? 'rgba(123,31,162,0.15)' : 'rgba(123,31,162,0.08)' }}>
                      {fmt(assetsTotal)} {currency}
                    </TableCell>
                    <TableCell colSpan={3} sx={{ bgcolor: isDarkMode ? 'rgba(123,31,162,0.15)' : 'rgba(123,31,162,0.08)' }} />
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        {/* Totals */}
        <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 2, border: `2px solid ${isDarkMode ? 'rgba(92,107,192,0.3)' : '#5c6bc0'}` }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 3, textAlign: 'center' }}>
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>{isRTL ? 'إجمالي المصروفات' : 'Total Expenses'}</Typography>
              <Typography variant="h5" fontWeight={800} color="primary">{fmt(settlement.totalExpenses)} <small>{currency}</small></Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>{isRTL ? 'المبلغ المسترد' : 'Returned'}</Typography>
              <Typography variant="h5" fontWeight={800} sx={{ color: '#e91e63' }}>{fmt(settlement.returnedAmount)} <small>{currency}</small></Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>{isRTL ? 'صافي التسوية' : 'Net'}</Typography>
              <Typography variant="h5" fontWeight={800} sx={{ color: '#4caf50' }}>{fmt(settlement.totalExpenses + settlement.returnedAmount)} <small>{currency}</small></Typography>
            </Box>
          </Box>
        </Paper>

        {settlement.notes && (
          <Paper elevation={1} sx={{ p: 2, mb: 2, borderRadius: 2, bgcolor: isDarkMode ? 'rgba(255,255,255,0.03)' : '#fff8e1' }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>{isRTL ? 'ملاحظات' : 'Notes'}</Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>{settlement.notes}</Typography>
          </Paper>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f5f5f5' }}>
        <Button onClick={onClose} size="large">{isRTL ? 'إغلاق' : 'Close'}</Button>
        <Button variant="contained" startIcon={<PrintIcon />} onClick={handlePrint} size="large"
          sx={{
            background: isDarkMode
              ? 'linear-gradient(45deg, #283593 30%, #5c6bc0 90%)'
              : 'linear-gradient(45deg, #5c6bc0 30%, #7986cb 90%)',
            fontWeight: 700
          }}>
          {isRTL ? 'طباعة' : 'Print'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CustodySettlementPreviewDialog;